import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Squiggle, SmileyDoodle } from "./doodles";
import { STATION } from "@/config/station";

const AMOUNTS = [
    { value: 1, label: "$1", desc: "Show Some Love." },
    { value: 3, label: "$3", desc: "Buy the Station a Coffee." },
    { value: 5, label: "$5", desc: "Keep the Signal Strong." },
    { value: 10, label: "$10", desc: "Help Us Grow." },
    { value: "custom", label: "Other", desc: "Your Choice." },
];

export default function Donations() {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(5);
    const [custom, setCustom] = useState("");

    const isCustom = selected === "custom";
    const customAmount = Number(custom);
    const customValid = custom !== "" && Number.isFinite(customAmount) && customAmount >= 1;
    const amount = isCustom ? customAmount : selected;
    const canDonate = !isCustom || customValid;

    const handleDonate = () => {
        if (!canDonate) return;
        toast.info("Preview only — no payment provider is connected yet, so nothing was charged.", {
            description: "When the owner connects a donation link, this button will open the real checkout.",
        });
    };

    return (
        <section
            id="show-love"
            data-testid="donations-section"
            className="border-y-[2.5px] border-ink bg-pinklight py-16 sm:py-24"
        >
            <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
                <Reveal>
                    <span className="sticker bg-punch text-white">05 · The Tip Jar</span>
                    <h2 className="mt-4 font-condensed text-3xl font-black uppercase tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Help Keep the Signal Alive.
                    </h2>
                    <Squiggle className="mx-auto mt-3 h-3 w-36" stroke="#FF6B35" />
                    <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink2 sm:text-lg">
                        If you enjoy what we're building, show the station a little love. Every
                        contribution helps us keep independent music moving and grow the vision. A
                        dollar is love, and every bit counts. No pressure — just good energy.
                    </p>
                </Reveal>

                <Reveal delay={0.15}>
                    <button
                        data-testid="donation-toggle-button"
                        onClick={() => setOpen((v) => !v)}
                        aria-expanded={open}
                        className="btn-ink mt-8 bg-sunny text-xl hover:bg-sunnyhover !px-8 !py-4"
                    >
                        <Heart className="h-5 w-5 fill-ink" strokeWidth={2.75} />
                        {open ? "Maybe Later" : "Show Some Love"}
                    </button>
                </Reveal>

                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            data-testid="donation-panel"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                        >
                            <div className="card-hard mt-8 bg-cream p-6 text-left sm:p-8">
                                <p className="font-mono2 text-xs font-bold uppercase tracking-[0.18em] text-ink2">
                                    Pick an amount — every bit counts
                                </p>
                                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                                    {AMOUNTS.map((a) => {
                                        const active = selected === a.value;
                                        return (
                                            <button
                                                key={String(a.value)}
                                                data-testid={`donation-amount-${a.value}`}
                                                onClick={() => setSelected(a.value)}
                                                aria-pressed={active}
                                                className={`rounded-xl border-[2.5px] border-ink p-4 text-left transition-[transform,box-shadow,background-color] duration-150 ${
                                                    active
                                                        ? "bg-sunny shadow-[4px_4px_0_#1A1918]"
                                                        : "bg-white shadow-[2px_2px_0_#1A1918] hover:bg-sunny/40"
                                                }`}
                                            >
                                                <span className="block font-condensed text-2xl font-black text-ink">
                                                    {a.label}
                                                </span>
                                                <span className="mt-1 block text-xs leading-snug text-ink2">
                                                    {a.desc}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {isCustom && (
                                    <div className="mt-4">
                                        <label
                                            htmlFor="custom-amount"
                                            className="font-mono2 text-xs font-bold uppercase tracking-[0.18em] text-ink2"
                                        >
                                            Your amount (USD)
                                        </label>
                                        <div className="mt-2 flex items-center gap-2">
                                            <span className="font-condensed text-2xl font-black text-ink">$</span>
                                            <input
                                                id="custom-amount"
                                                data-testid="donation-custom-amount-input"
                                                type="number"
                                                min="1"
                                                step="1"
                                                inputMode="numeric"
                                                value={custom}
                                                onChange={(e) => setCustom(e.target.value)}
                                                placeholder="e.g. 7"
                                                className="input-ink max-w-[160px]"
                                            />
                                        </div>
                                        {custom !== "" && !customValid && (
                                            <p data-testid="donation-custom-error" className="mt-2 text-sm font-semibold text-red-600">
                                                Enter at least $1.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {STATION.donationCheckoutUrl ? (
                                    <a
                                        data-testid="donation-submit-button"
                                        href={STATION.donationCheckoutUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`btn-ink mt-6 w-full bg-sunny text-xl hover:bg-sunnyhover ${
                                            canDonate ? "" : "pointer-events-none cursor-not-allowed opacity-50"
                                        }`}
                                    >
                                        <HandCoins className="h-5 w-5" strokeWidth={2.5} />
                                        {canDonate ? `Donate $${amount} via Ko-fi` : "Donate"}
                                    </a>
                                ) : (
                                    <button
                                        data-testid="donation-submit-button"
                                        onClick={handleDonate}
                                        disabled={!canDonate}
                                        className={`btn-ink mt-6 w-full bg-sunny text-xl hover:bg-sunnyhover ${
                                            canDonate ? "" : "cursor-not-allowed opacity-50"
                                        }`}
                                    >
                                        <HandCoins className="h-5 w-5" strokeWidth={2.5} />
                                        {canDonate ? `Donate $${amount}` : "Donate"}
                                    </button>
                                )}

                                {STATION.donationCheckoutUrl ? (
                                    <p
                                        data-testid="donation-checkout-note"
                                        className="mt-4 text-center font-mono2 text-xs text-ink2"
                                    >
                                        Opens our Ko-fi page in a new tab to complete your {canDonate ? `$${amount}` : ""} contribution.
                                    </p>
                                ) : (
                                    <p
                                        data-testid="donation-preview-badge"
                                        className="mt-4 rounded-lg border-2 border-dashed border-ink/40 bg-white/70 px-3 py-2 text-center font-mono2 text-xs text-ink2"
                                    >
                                        Preview mode — a real payment provider hasn't been connected
                                        yet. No charge is made when you click.
                                    </p>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <Reveal delay={0.2}>
                    <div className="mt-8 flex items-center justify-center gap-3">
                        <SmileyDoodle className="h-10 w-10" fill="#4EDBA2" />
                        <p className="font-hand text-2xl font-bold text-ink2">
                            listening is always free. always.
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
